<template>
    <div class="toolbar-wrap">
        <div>
            <p class="field-title" style="margin-left: 0.5rem">
                Vendor Agreements
                <Tooltip description="Manage your company's distributors and their commission rates." />
            </p>
        </div>
    </div>
    <div class="table-wrap">
        <table class="distributors-table" v-if="distributors?.length">
            <thead>
                <tr>
                    <th>Agreement Owner</th>
                    <th>Owner's Contact Name</th>
                    <th>Owner's Contact Email</th>
                    <th>Owner's Contact Phone</th>
                    <th v-if="!isPartner">Commission Rate</th>
                    <th v-if="!isPartner">Notes</th>
                    <th>Contacts</th>
                    <th v-if="!isPartner">Default</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(distributor, index) in distributors" :key="index">
                    <td>{{ getChannelName(distributor.subnetwork_id) }}</td>
                    <td>{{ distributor.contact_name || 'N/A' }}</td>
                    <td>{{ distributor.contact_email || 'N/A' }}</td>
                    <td>{{ distributor.contact_phone || 'N/A' }}</td>
                    <td v-if="!isPartner">
                        <span v-if="!distributor.isOwner">{{ formatCommissionRateForDisplay(convertPercentageToNumber(distributor.commission_rate)) }}</span>
                        <div v-else class="editable-field-container">
                            <span v-if="!distributor.isEditingCommissionRate" class="editable-text">
                                {{ formatCommissionRateForDisplay(convertPercentageToNumber(distributor.commission_rate)) }}
                                <i class="pi pi-pencil edit-icon" @click="startEditingCommissionRate(distributor)" title="Click to edit"></i>
                            </span>
                            <div v-else class="edit-input-container">
                                <InputText 
                                    v-model="distributor.commission_rate_numeric" 
                                    placeholder="Enter commission rate (e.g., 15.5)"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    @input="onCommissionRateInput"
                                    @blur="finishEditingCommissionRate(distributor)"
                                    ref="commissionRateInput"
                                    style="max-width: 7rem;"
                                    :class="{ 'error-input': !validateCommissionRate(distributor.commission_rate_numeric) }"
                                />
                                <small>%</small>
                                <i class="pi pi-check save-icon" @click="finishEditingCommissionRate(distributor)" title="Save"></i>
                                <small v-if="!validateCommissionRate(distributor.commission_rate_numeric)" class="error-msg">
                                    Please enter a valid positive number
                                </small>
                            </div>
                        </div>
                    </td>
                    <td v-if="!isPartner">
                        <span v-if="!distributor.isOwner" class="notes-text">{{ distributor.notes || 'N/A' }}</span>
                        <div v-else class="editable-field-container">
                            <span v-if="!distributor.isEditingNotes" class="editable-text">
                                <span class="notes-text">{{ distributor.notes || 'N/A' }}</span>
                                <i class="pi pi-pencil edit-icon" @click="startEditingNotes(distributor)" title="Click to edit"></i>
                            </span>
                            <div v-else class="edit-input-container notes-edit">
                                <Textarea 
                                    v-model="distributor.notes" 
                                    placeholder="Enter notes"
                                    rows="3"
                                    @input="onDistributorFieldChange"
                                    @blur="finishEditingNotes(distributor)"
                                    ref="notesInput"
                                />
                                <i class="pi pi-check save-icon" @click="finishEditingNotes(distributor)" title="Save"></i>
                            </div>
                        </div>
                    </td>

                    <td class="center-column">
                        <div style="display: flex; width: 80px;">
                        <div  v-if="distributor.isOwner || isPartner || (isAdmin && platform === 'ArenaCX')">
                            <Button 
                                icon="pi pi-users" 
                                class="p-button-rounded p-button-warning"
                                @click="openDistributorContactsModal(distributor, index)"
                                :title="`View contacts for ${getChannelName(distributor.subnetwork_id)}`"
                            />
                            <span class="contact-count" v-if="distributor.contacts && distributor.contacts.length > 0">
                                ({{ distributor.contacts.length }})
                            </span>

                        </div>
                        <div v-else>
                        <Button 
                            icon="pi pi-users" 
                            class="p-button-rounded p-button-warning"
                            style="cursor: default; opacity: 0.5; pointer-events: none;"
                        />
                        </div>
                        </div> 
                    
                    </td>

                    <td class="center-column" v-if="!isPartner">
                        <Dropdown 
                            v-model="distributor.is_default" 
                            :options="defaultOptions" 
                            optionLabel="label" 
                            optionValue="value"
                            @change="onDefaultChange(distributor, $event)"
                            class="default-dropdown"
                        />
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Distributor Contacts Modal -->
        <Dialog :draggable="false" v-model:visible="distributorContactsDialog" :style="{ width: '900px' }" header="Distributor Contacts" :modal="true" class="p-fluid">
            <div class="toolbar-wrap">
                <div>
                    <p class="field-title" style="margin-left: 0.5rem">
                        Contacts for {{ getChannelName(currentDistributor?.subnetwork_id) }}
                    </p>
                </div>
                <Button v-if="isOwner || isPartner" label="Add Contact" class="mr-2 inline-block add-new-btn" @click="openNewContact" />
            </div>

            <div class="table-wrap">
                <table class="contacts-table" v-if="distributorContacts?.length">
                    <thead>
                        <tr>
                            <th v-if="isOwner || isPartner">Actions</th>
                            <th>Name</th>
                            <th>Title</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>
                                Primary Contact
                                <Tooltip description="This is the person that RFPs will be sent to." />
                            </th>
                            <th>RFP Contributor</th>
                            <th>RFP Invitation Recipient</th>
                            <th>Escalation Contact</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(contact, index) in distributorContacts" :key="index">
                            <td class="actions-column" v-if="isOwner || isPartner">
                                <div class="actions-column-inner">
                                    <Button icon="pi pi-pencil" class="p-button-rounded p-button-warning mr-2"
                                        @click="editContact(contact, index)" />
                                    <Button 
                                        icon="pi pi-trash" 
                                        class="p-button-rounded p-button-warning"
                                        @click="deleteContact(contact, index)"/>
                                </div>
                            </td>
                            <td>{{ contact.name }}</td>
                            <td>{{ contact.title }}</td>
                            <td>
                                {{ contact.email }}
                                <EmailDeliverabilityBadge
                                    :status="contact.deliverability_status"
                                    :last-bounced-at="contact.last_bounced_at"
                                    :last-diagnostic="contact.last_diagnostic"
                                />
                            </td>
                            <td>{{ contact.phone }}</td>
                            <td class="center-column">
                                <i :class="['pi', contact.isPrimary ? 'pi-check' : 'pi-times']"></i>
                            </td>
                            <td class="center-column">
                                <i :class="['pi', contact.isRFPContributor ? 'pi-check' : 'pi-times']"></i>
                            </td>
                            <td class="center-column">
                                <i :class="['pi', contact.isRFPInvitationRecipient ? 'pi-check' : 'pi-times']"></i>
                            </td>
                            <td class="center-column">
                                <i :class="['pi', contact.isEscalation ? 'pi-check' : 'pi-times']"></i>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div v-else class="no-contacts">
                    <p>No contacts found for this distributor. Click "Add Contact" to add one.</p>
                </div>
            </div>

            <!-- Contact Form Dialog -->
            <Dialog :draggable="false" v-model:visible="contactDialog" :style="{ width: '600px' }" header="Contact Details" :modal="true" class="p-fluid">
                <div class="field">
                    <label for="contact_name">Name</label>
                    <InputText id="contact_name" v-model="contact.name" required="true" />
                </div>

                <div class="field">
                    <label for="contact_title">Title</label>
                    <InputText id="contact_title" v-model="contact.title" required="true" />
                </div>

                <div class="field">
                    <label for="contact_email">Email</label>
                    <InputText id="contact_email" v-model="contact.email" required="true" type="email" />
                    <small v-if="contact.email && !isValidEmail(contact.email)" class="error-msg">
                        Please enter a valid email address
                    </small>
                </div>

                <div class="field">
                    <label for="contact_phone">Phone</label>
                    <InputText id="contact_phone" v-model="contact.phone" required="true" maxlength="25" @input="validateContactPhoneInput" />
                    <small v-if="contact.phone && !isValidPhone(contact.phone)" class="error-msg">
                        Please enter a valid phone number (e.g., (123) 123-1234, 123-123-1234, +1 415 555 2671)
                    </small>
                </div>

                <div class="field-checkbox mb-1">
                    <Checkbox id="contact_primary" v-model="contact.isPrimary" :binary="true" 
                        @change="handleContactPrimaryChange" />
                    <label for="contact_primary" style="margin-left: 1rem">Set as Primary Contact</label>
                </div>

                <div class="field-checkbox mb-1">
                    <Checkbox id="contact_rfp_contributor" v-model="contact.isRFPContributor" :binary="true" 
                        @change="handleContactRFPContributorChange" />
                    <label for="contact_rfp_contributor" style="margin-left: 1rem">Set as RFP Contributor</label>
                </div>

                <div class="field-checkbox mb-1">
                    <Checkbox id="contact_rfp_invitation" v-model="contact.isRFPInvitationRecipient" :binary="true" 
                        @change="handleContactRFPInvitationRecipientChange" />
                    <label for="contact_rfp_invitation" style="margin-left: 1rem">Set as RFP Invitation Recipient</label>
                </div>

                <div class="field-checkbox">
                    <Checkbox id="contact_escalation" v-model="contact.isEscalation" :binary="true" />
                    <label for="contact_escalation" style="margin-left: 1rem">Set as Escalation Contact</label>
                </div>

                <template #footer>
                    <Button label="Cancel" class="p-button p-component p-button-outlined button" @click="hideContactDialog" />
                    <Button v-if="contactEditIndex === null" label="Add" class="p-button p-component button"
                        @click="onContactSave" />
                    <Button v-else label="Update" class="p-button p-component button" @click="onContactSave" />
                </template>
            </Dialog>

            <!-- Primary Contact Replacement Confirmation Dialog -->
            <Dialog :draggable="false" v-model:visible="showPrimaryContactReplaceDialog" :style="{ width: '500px' }" header="A Primary Contact Already Exists." :modal="true"  @hide="cancelPrimaryContactReplace">
                <div class="confirmation-content">
                    <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
                    <span>
                        You can either delete the existing contact or replace it. If you replace it, the current Primary Contact will be downgraded to an RFP Contributor.
                    </span>
                </div>
                <template #footer>
                    <Button label="Cancel" class="p-button p-component p-button-outlined button"
                        @click="cancelPrimaryContactReplace" />
                    <Button label="Delete Existing" class="p-button p-component p-button-danger button" 
                style="width: 9rem !important;"
                        @click="deleteExistingPrimaryContact"  />
                    <Button label="Replace" class="p-button p-component button" @click="confirmPrimaryContactReplace" />
                </template>
            </Dialog>

            <!-- Delete Contact Confirmation Dialog -->
            <Dialog :draggable="false" v-model:visible="deleteContactDialog" :style="{ width: '450px' }" header="Confirm" :modal="true">
                <div class="confirmation-content">
                    <i class="pi pi-exclamation-triangle mr-3" style="font-size: 1.75rem; margin-right: 0.5rem" />
                    <span v-if="contact">Are you sure you want to delete <b>{{ contact.name }}</b>?</span>
                </div>
                <template #footer>
                    <Button label="No" class="p-button p-component p-button-outlined button"
                        @click="deleteContactDialog = false" />
                    <Button label="Yes" class="p-button p-component button" @click="onConfirmDeleteContact" />
                </template>
            </Dialog>

            <!-- Email Change Warning Dialog -->
            <Dialog :draggable="false" v-model:visible="showEmailChangeDialog" :style="{ width: '500px' }" header="Confirm Email Change" :modal="true">
                <div class="confirmation-content">
                    <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
                    <div>
                        <p style="margin: 0; font-size: 1rem; line-height: 1.5;">
                            You have changed the email address. The user associated with the old email will be deleted, and an invitation will be sent to the new email provided.
                        </p>
                        <p style="margin: 0.5rem 0 0 0; font-weight: 600;">
                            Are you sure you want to proceed?
                        </p>
                    </div>
                </div>
                <template #footer>
                    <Button label="Cancel" class="p-button p-component p-button-outlined button"
                        @click="cancelEmailChange" />
                    <Button label="Proceed" class="p-button p-component button" @click="confirmEmailChange" />
                </template>
            </Dialog>
        </Dialog>
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import Tooltip from './Tooltip.vue';
import { getAccount, getIsPartner, getSubnetworkId } from '../service/common';
import { Dropdown } from 'primevue/dropdown';
import Textarea from 'primevue/textarea';
import EmailDeliverabilityBadge from './EmailDeliverabilityBadge.vue';

export default {
    name: 'ProfileDistributors',
    props: ['saveInd', 'isChannel', 'distributorValues', 'channels', "partnerOwner"],
    components: {
        Tooltip,
        Dropdown,
        Textarea,
        EmailDeliverabilityBadge,
    },
    data() {
        return {
            distributors: [],
            accountId: null,
            isAdmin: getAccount(),
            isPartner: getIsPartner(),
            subnetworkID: getSubnetworkId(),
            isSaveValidation: false,
            // Distributor contacts related data
            distributorContactsDialog: false,
            distributorContacts: [],
            currentDistributor: null,
            contactDialog: false,
            contact: {},
            contactEditIndex: null,
            deleteContactDialog: false,
            deleteContactIndex: null,
            showPrimaryContactReplaceDialog: false,
            primaryContactReplaceConfirmed: false, // Track if user confirmed replacement
            showEmailChangeDialog: false, // Track email change warning dialog
            originalEmail: '', // Store original email for comparison
            DEFAULT_SUBNETWORK_ID: process.env.VUE_APP_DEFAULT_SUBNETWORK_ID,
            platform: process.env.VUE_APP_PLATFORM_NAME,
            isOwner: false,
            defaultOptions: [
                { label: 'Yes', value: true },
                { label: 'No', value: false }
            ],
        };
    },
    created() {
        this.accountId = localStorage.getItem('accountId');
        console.log('Channels:', this.channels);
        console.log('Account ID:', this.accountId);
    },
    setup() {
        const v = useVuelidate({ $stopPropagation: true });
        return { v };
    },
            watch: {
            saveInd() {
                if (this.saveInd) {
                    this.isSaveValidation = true;
                    this.$nextTick(() => {
                        this.validationCall();
                        this.isSaveValidation = false;
                    });
                }
            },
            distributorValues: {
                immediate: true,
                handler(newVal) {
                    try {
                        this.distributors = newVal || [];
                        
                        // Ensure all distributors have contacts array
                        this.distributors.forEach(distributor => {
                            if (!distributor.contacts) {
                                distributor.contacts = [];
                            }
                        });
                    } catch (err) {
                        console.error('Error in distributorValues watch:', err);
                    }
                }
            },
        },
    methods: {
        // Commission rate utility functions
        convertPercentageToNumber(percentageString) {
            if (!percentageString || percentageString === 'N/A') return null;
            // Remove % sign and convert to number
            const numericValue = parseFloat(percentageString.toString().replace('%', ''));
            return isNaN(numericValue) ? null : numericValue;
        },

        convertNumberToPercentage(numericValue) {
            if (numericValue === null || numericValue === undefined || numericValue === '') return '0%';
            const num = parseFloat(numericValue);
            return isNaN(num) ? '0%' : `${num}%`;
        },

        validateCommissionRate(value) {
            if (!value || value === '') return true; // Allow empty values
            const num = parseFloat(value);
            return !isNaN(num) && num >= 0;
        },

        formatCommissionRateForDisplay(numericValue) {
            if (numericValue === null || numericValue === undefined || numericValue === '') return '0 %';
            return `${numericValue} %`;
        },

        isValidEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        },

        isValidPhone(phone) {
            // This regex allows:
            // - Optional + at start
            // - Optional country code
            // - Optional spaces, parentheses, hyphens, and dots
            // - Between 10 and 15 digits total (including country code)
            const phoneRegex = /^\+?[0-9\s\-().]{10,15}$/;
            return phoneRegex.test(phone);
        },

        validationCall() {
            this.distributors.forEach(distributor => {
                if (!distributor.contacts) {
                    distributor.contacts = [];
                }
            });

            // Always emit the current state
            this.$emit('submit', false, this.distributors);
        },

        getChannelName(subnetwork_id) {
            const channel = this.channels.find(c => c.subnetwork_id === subnetwork_id);
            return channel ? channel.name : 'Unknown';
        },

        // Distributor contacts methods
        openDistributorContactsModal(distributor, index) {
            this.isOwner = distributor.isOwner;
            this.currentDistributor = distributor;
            this.distributorContacts = distributor.contacts || [];
            this.distributorContactsDialog = true;
        },

        openNewContact() {
            this.contact = {
                name: '',
                title: '',
                email: '',
                phone: '',
                isPrimary: false,
                isEscalation: false,
                isRFPContributor: false,
                isRFPInvitationRecipient: false
            };
            this.contactEditIndex = null;
            this.contactDialog = true;
        },

        editContact(contact, index) {
            this.contact = { ...contact };
            this.contactEditIndex = index;
            this.originalEmail = contact.email; // Store original email for comparison
            this.contactDialog = true;
        },

        deleteContact(contact, index) {
            this.contact = { ...contact };
            this.deleteContactIndex = index;
            this.deleteContactDialog = true;
        },

        hideContactDialog() {
            this.contactDialog = false;
            this.contact = {};
            this.contactEditIndex = null;
            // Reset the confirmation flag when dialog is closed
            this.primaryContactReplaceConfirmed = false;
            // Reset original email
            this.originalEmail = '';
        },

        onConfirmDeleteContact() {
            // Check if this is the last contact for this distributor
            if (this.distributorContacts.length <= 1) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Cannot Delete Contact',
                    detail: 'At least one contact must remain for each distributor',
                    life: 3000,
                });
                this.deleteContactDialog = false;
                this.contact = {};
                this.deleteContactIndex = null;
                return;
            }

            this.distributorContacts.splice(this.deleteContactIndex, 1);
            this.deleteContactDialog = false;
            this.contact = {};
            this.deleteContactIndex = null;
            this.updateDistributorContacts();
        },

        handleContactPrimaryChange(event) {
            console.log("Event:: ", event)
            if (event.isTrusted === true) {
                const existingPrimaryIndex = this.distributorContacts.findIndex(c => 
                    c.isPrimary && (this.contactEditIndex === null || this.distributorContacts.indexOf(c) !== this.contactEditIndex)
                );

                
                if (existingPrimaryIndex !== -1) {
                    // Show confirmation dialog to replace existing primary contact
                    this.showPrimaryContactReplaceDialog = true;
                    return; // Don't proceed until user confirms
                }
                
                // If no existing primary or confirmed, proceed with setting as primary
                // Auto-select RFP Contributor and RFP Invitation Recipient when Primary Contact is selected
                this.contact.isRFPContributor = true;
                this.contact.isRFPInvitationRecipient = true;
            }
        },

        handleContactRFPContributorChange() {
            // If trying to deselect RFP Contributor while Primary Contact is selected, prevent it
            if (!this.contact.isRFPContributor && this.contact.isPrimary) {
                this.contact.isRFPContributor = true; // Re-enable RFP Contributor
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Cannot Deselect RFP Contributor',
                    detail: 'Primary Contact must be deselected first before deselecting RFP Contributor',
                    life: 3000,
                });
            }
            
            // If RFP Contributor is deselected, also deselect RFP Invitation Recipient
            if (!this.contact.isRFPContributor && !this.contact.isPrimary) {
                this.contact.isRFPInvitationRecipient = false;
            }
        },

        handleContactRFPInvitationRecipientChange() {
            // If trying to deselect RFP Invitation Recipient while Primary Contact is selected, prevent it
            if (!this.contact.isRFPInvitationRecipient && this.contact.isPrimary) {
                this.contact.isRFPInvitationRecipient = true; // Re-enable RFP Invitation Recipient
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Cannot Deselect RFP Invitation Recipient',
                    detail: 'Primary Contact must be deselected first before deselecting RFP Invitation Recipient',
                    life: 3000,
                });
                return;
            }
            
            // RFP Invitation Recipient can only be checked if Primary Contact and/or RFP Contributor is checked
            if (this.contact.isRFPInvitationRecipient) {
                if (!this.contact.isPrimary && !this.contact.isRFPContributor) {
                    this.contact.isRFPInvitationRecipient = false; // Uncheck it
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Cannot Select RFP Invitation Recipient',
                        detail: 'RFP Invitation Recipient can only be selected if Primary Contact and/or RFP Contributor is selected',
                        life: 3000,
                    });
                }
            }
        },

        cancelPrimaryContactReplace() {
            this.showPrimaryContactReplaceDialog = false;
            // Uncheck the primary contact checkbox since user declined to replace
            this.contact.isPrimary = false;
            // Reset the confirmation flag
            this.primaryContactReplaceConfirmed = false;
        },

        confirmPrimaryContactReplace() {
            this.showPrimaryContactReplaceDialog = false;
            
            // Find existing primary contact and convert to RFP Contributor
            const existingPrimaryIndex = this.distributorContacts.findIndex(c => c.isPrimary);
            if (existingPrimaryIndex !== -1) {
                this.distributorContacts[existingPrimaryIndex].isPrimary = false;
                this.distributorContacts[existingPrimaryIndex].isRFPContributor = true;
            }
            
            // Set the current contact form as primary (use $nextTick to avoid triggering change event)
            this.$nextTick(() => {
                this.contact.isPrimary = true;
                
                // Auto-select RFP Contributor and RFP Invitation Recipient when Primary Contact is selected
                this.contact.isRFPContributor = true;
                this.contact.isRFPInvitationRecipient = true;
                
                // Set the confirmation flag to skip redundant validation in onContactSave()
                this.primaryContactReplaceConfirmed = true;
            });
        },

        deleteExistingPrimaryContact() {
            this.showPrimaryContactReplaceDialog = false;
            
            // Check if this is the last contact and we're not currently adding a new one (only editing)
            // If editing the same contact index, we need at least 1 contact
            // If adding new, deletion is okay since we'll add the new one
            const isAddingNew = this.contactEditIndex === null;
            
            if (this.distributorContacts.length <= 1 && !isAddingNew) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Cannot Delete Contact',
                    detail: 'At least one contact must remain for each distributor. Please add the new contact first.',
                    life: 3000,
                });
                // Reset the primary checkbox since deletion was prevented
                this.contact.isPrimary = false;
                this.primaryContactReplaceConfirmed = false;
                return;
            }
            
            // Find and delete the existing primary contact
            const existingPrimaryIndex = this.distributorContacts.findIndex(c => c.isPrimary);
            if (existingPrimaryIndex !== -1) {
                const deletedContactName = this.distributorContacts[existingPrimaryIndex].name;
                this.distributorContacts.splice(existingPrimaryIndex, 1);
                
                this.$toast.add({
                    severity: 'success',
                    summary: 'Contact Deleted',
                    detail: `${deletedContactName} has been deleted successfully`,
                    life: 3000,
                });
                
                // Update the distributor contacts after deletion
                this.updateDistributorContacts();
            }
            
            // Set the current contact form as primary (use $nextTick to avoid triggering change event)
            this.$nextTick(() => {
                this.contact.isPrimary = true;
                
                // Auto-select RFP Contributor and RFP Invitation Recipient when Primary Contact is selected
                this.contact.isRFPContributor = true;
                this.contact.isRFPInvitationRecipient = true;
                
                // Set the confirmation flag to skip redundant validation in onContactSave()
                this.primaryContactReplaceConfirmed = true;
            });
        },

        validateContactPhoneInput() {
            // Remove any non-digit characters except +, (, ), -, and space
            this.contact.phone = this.contact.phone.replace(/[^\d+\-()\s]/g, '');
        },

        onContactSave() {
            // Required field validations
            if (!this.contact.name || this.contact.name.trim() === '') {
                return this.$toast.add({
                    severity: 'error',
                    summary: 'Required Field',
                    detail: 'Name is required',
                    life: 3000,
                });
            }

            if (!this.contact.email) {
                return this.$toast.add({
                    severity: 'error',
                    summary: 'Required Field',
                    detail: 'Email is required',
                    life: 3000,
                });
            }

            // Email format validation
            if (!this.isValidEmail(this.contact.email)) {
                return this.$toast.add({
                    severity: 'error',
                    summary: 'Invalid Format',
                    detail: 'Please enter a valid email address',
                    life: 3000,
                });
            }

            if (!this.contact.phone) {
                return this.$toast.add({
                    severity: 'error',
                    summary: 'Required Field',
                    detail: 'Phone number is required',
                    life: 3000,
                });
            }

            // Phone format validation
            if (!this.isValidPhone(this.contact.phone)) {
                return this.$toast.add({
                    severity: 'error',
                    summary: 'Invalid Format',
                    detail: 'Please enter a valid phone number (e.g., (123) 123-1234, 123-123-1234, +1 415 555 2671)',
                    life: 3000,
                });
            }

            // Check for email change when editing an existing primary contact
            if (this.contactEditIndex !== null && this.contact.isPrimary && this.originalEmail && this.originalEmail.trim().toLowerCase() !== this.contact.email.trim().toLowerCase()) {
                // Email has changed for a primary contact, show warning dialog
                this.showEmailChangeDialog = true;
                return; // Don't proceed with save until user confirms
            }

            // If no email change detected, proceed with normal save
            this.proceedWithContactSave();
        },

        updateDistributorContacts() {
            // Update the distributor's contacts in the main distributors array
            if (this.currentDistributor) {
                const distributorIndex = this.distributors.findIndex(d => 
                    d.distributor_id === this.currentDistributor.distributor_id
                );
                if (distributorIndex !== -1) {
                    this.distributors[distributorIndex].contacts = [...this.distributorContacts];
                }
            }
            
            // Emit changes to parent component to trigger save button visibility
            this.$emit('submit', false, this.distributors);
        },

        onDistributorFieldChange() {
            // Emit changes to parent component when commission rate or notes are modified
            this.$emit('submit', false, this.distributors);
        },

        startEditingCommissionRate(distributor) {
            // Convert percentage string to numeric value for editing
            distributor.commission_rate_numeric = this.convertPercentageToNumber(distributor.commission_rate);
            // Set editing state for commission rate
            distributor.isEditingCommissionRate = true;
            // Focus the input field after it's rendered
            this.$nextTick(() => {
                if (this.$refs.commissionRateInput) {
                    this.$refs.commissionRateInput.$el.focus();
                }
            });
        },

        finishEditingCommissionRate(distributor) {
            // Validate the input before saving
            if (!this.validateCommissionRate(distributor.commission_rate_numeric)) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Invalid Commission Rate',
                    detail: 'Please enter a valid positive number',
                    life: 3000,
                });
                return;
            }

            // Convert numeric value back to percentage string for storage
            distributor.commission_rate = this.convertNumberToPercentage(distributor.commission_rate_numeric);
            // Remove editing state for commission rate
            distributor.isEditingCommissionRate = false;
            // Emit changes
            this.onDistributorFieldChange();
        },

        onCommissionRateInput() {
            // Real-time validation feedback
            this.onDistributorFieldChange();
        },

        startEditingNotes(distributor) {
            // Set editing state for notes
            distributor.isEditingNotes = true;
            // Focus the input field after it's rendered
            this.$nextTick(() => {
                if (this.$refs.notesInput) {
                    this.$refs.notesInput.$el.focus();
                }
            });
        },

        finishEditingNotes(distributor) {
            // Remove editing state for notes
            distributor.isEditingNotes = false;
            // Emit changes
            this.onDistributorFieldChange();
        },

        onDefaultChange(selectedDistributor, event) {
            const newValue = event.value;
            
            // If setting this distributor as default (true)
            if (newValue === true) {
                // Set all other distributors to false
                this.distributors.forEach(distributor => {
                    if (distributor.distributor_id !== selectedDistributor.distributor_id) {
                        distributor.is_default = false;
                    }
                });
            }
            
            // Update the selected distributor's value
            selectedDistributor.is_default = newValue;
            
            // Emit changes to parent component
            this.onDistributorFieldChange();
        },

        // Email change warning dialog methods
        cancelEmailChange() {
            this.showEmailChangeDialog = false;
            // Reset the email to original value
            this.contact.email = this.originalEmail;
        },

        confirmEmailChange() {
            this.showEmailChangeDialog = false;
            // Proceed with the save operation
            this.proceedWithContactSave();
        },

        proceedWithContactSave() {
            // This method contains the rest of the original onContactSave logic
            // Check for existing primary contact (skip if user already confirmed replacement)
            if (!this.primaryContactReplaceConfirmed) {
                const existingPrimary = this.distributorContacts.some(c => 
                    c.isPrimary && (this.contactEditIndex === null || this.distributorContacts.indexOf(c) !== this.contactEditIndex)
                );
                
                if (this.contact.isPrimary && existingPrimary) {
                    return this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Only one primary contact is allowed per distributor',
                        life: 3000,
                    });
                }
            }

            // Ensure RFP Invitation Recipient is selected if Primary Contact is selected
            if (this.contact.isPrimary && !this.contact.isRFPInvitationRecipient) {
                this.contact.isRFPInvitationRecipient = true;
            }

            // Email uniqueness validation
            const normalizedNewEmail = this.contact.email.trim().toLowerCase();
            const isDuplicate = this.distributorContacts.some((contact, index) => {
                // Skip the current contact if we're editing
                if (this.contactEditIndex === index) return false;
                
                return contact.email && contact.email.trim().toLowerCase() === normalizedNewEmail;
            });

            if (isDuplicate) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Duplicate Email',
                    detail: 'This email address is already used by another contact',
                    life: 3000,
                });
                return;
            }

            // Trim all string values
            if (this.contact.name) this.contact.name = this.contact.name.trim();
            if (this.contact.title) this.contact.title = this.contact.title.trim();
            if (this.contact.email) this.contact.email = this.contact.email.trim();
            if (this.contact.phone) this.contact.phone = String(this.contact.phone).trim();

            if (this.contactEditIndex === null) {
                this.distributorContacts.push({ ...this.contact });
            } else {
                this.distributorContacts.splice(this.contactEditIndex, 1, { ...this.contact });
                this.contactEditIndex = null;
            }

            // Reset the confirmation flag after successful save
            this.primaryContactReplaceConfirmed = false;
            // Reset original email
            this.originalEmail = '';

            this.hideContactDialog();
            this.updateDistributorContacts();
        },
    },
};
</script>

<style scoped lang="scss">
.add-new-btn {
    margin-top: 1.6rem !important;
    width: 8rem !important;
    background: #00796b !important;
    border: 2px solid #00796b !important;
    display: block;
    float: right !important;
}

::v-deep(.p-button.p-button-outlined) {
    background-color: transparent !important;
    color: #00796b !important;
    border: 1px solid;
}

::v-deep(.p-button.p-button-danger) {
    background-color: #dc3545 !important;
    color: #ffffff !important;
    border: 1px solid #dc3545 !important;
}

::v-deep(.p-button.p-button-danger:hover) {
    background-color: #c82333 !important;
    border-color: #bd2130 !important;
}

::v-deep(.p-button.p-button-icon-only.p-button-rounded) {
    border-radius: 50%;
    height: 2.5rem;
    width: 2.5rem !important;
    margin: 0.5rem;
    background: #ffffff !important;
    border: none !important;
    display: flex;
    float: left !important;
}

::v-deep(.p-button.p-button-icon-only.p-button-rounded):hover {
    background: #e4edd9 !important;
}

.field {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    margin-bottom: 0.75rem;
}

.field-title {
    margin-left: 1rem;
    font-size: 1.2rem;
    font-weight: 400;
    max-width: 30rem;
}

.confirmation-content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 1rem;
}

.table-wrap {
    overflow-x: scroll;
    margin-top: 6rem;
    margin-bottom: 4rem;
    overflow-x: scroll;
}

.toolbar-wrap {
    width: 100%;
    background: #e4edd9;
    align-self: center;
    padding: 0.75rem;
    border-radius: 0.5rem;
    margin-top: 2rem;
}

.error-msg {
    margin-left: 1.25rem;
    color: red;
    font-size: 1rem;
    font-weight: 400;
}

.distributors-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.distributors-table th,
.distributors-table td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #eee;
    max-width: 200px;
    word-wrap: break-word;
    white-space: normal;
}

.distributors-table th {
    background: #fbfbfb;
    color: #707683;
    font-weight: 500;
}

.distributors-table tr:hover {
    background-color: #f8f9fa;
}

.actions-column-inner {
    display: flex;
}

.center-column {
    text-align: center;
}

.center-column i {
    font-size: 1.1rem;
}

.pi-check {
    color: #00796b;
}

.pi-times {
    color: #707683;
}

.readonly-field {
    background-color: #f8f9fa !important;
    color: #6c757d !important;
    cursor: not-allowed !important;
}

.contacts-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.contacts-table th,
.contacts-table td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #eee;
    max-width: 200px;
    word-wrap: break-word;
    white-space: normal;
}

.contacts-table th {
    background: #fbfbfb;
    color: #707683;
    font-weight: 500;
}

.no-contacts {
    text-align: center;
    padding: 2rem;
    color: #707683;
    font-style: italic;
}

.mb-1 {
    margin-bottom: 1rem;
}

.contact-count {
    margin-left: 0.5rem;
    font-size: 0.875rem;
    color: #00796b;
    font-weight: 500;
}

.editable-field-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.editable-text {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
}

.editable-text:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    margin: -0.25rem -0.5rem;
}

.edit-icon {
    color: #00796b;
    font-size: 0.875rem;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s ease;
}

.edit-icon:hover {
    opacity: 1;
}

.edit-input-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.edit-input-container input {
    border: 1px solid #00796b;
    border-radius: 4px;
    padding: 0.5rem;
    font-size: 0.875rem;
}

.edit-input-container input:focus {
    outline: none;
    border-color: #005a4f;
    box-shadow: 0 0 0 2px rgba(0, 121, 107, 0.2);
}

.save-icon {
    color: #00796b;
    font-size: 0.875rem;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s ease;
}

.save-icon:hover {
    opacity: 1;
}

.error-input {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2) !important;
}

.error-input:focus {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2) !important;
}

.default-dropdown {
    min-width: 80px;
}


::v-deep(.default-dropdown .p-dropdown) {
    min-width: 80px;
    height: 2rem;
}

::v-deep(.default-dropdown .p-dropdown-label) {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
}

::v-deep(.default-dropdown .p-dropdown:focus) {
    border: 0;
    outline: 0;
}

::v-deep(.default-dropdown .p-dropdown:focus-visible) {
    border: 0;
    outline: 0;
}

.notes-text {
    white-space: pre-wrap;
    word-wrap: break-word;
}

.notes-edit {
    width: 100%;
}

.notes-edit textarea {
    width: 100%;
    border: 1px solid #00796b;
    border-radius: 4px;
    padding: 0.5rem;
    font-size: 0.875rem;
    resize: vertical;
    min-height: 60px;
}

.notes-edit textarea:focus {
    outline: none;
    border-color: #005a4f;
    box-shadow: 0 0 0 2px rgba(0, 121, 107, 0.2);
}

// .notes-edit .save-icon {
//     align-self: flex-end;
//     margin-top: 0.25rem;
// }

</style>
